import { supabase } from '@/lib/supabase'

export type Tag = {
  id: string
  name: string
  color: string
  created_by: string | null
  created_at: string
  updated_at: string
}

export async function fetchTags(): Promise<Tag[]> {
  const { data, error } = await supabase
    .from('tags')
    .select('*')
    .order('name')

  if (error) throw new Error(error.message)
  return (data ?? []) as Tag[]
}

export async function createTag({ name, color }: { name: string; color: string }): Promise<Tag> {
  const { data: { user } } = await supabase.auth.getUser()
  if (!user) throw new Error('Unauthorized')

  const { data, error } = await supabase
    .from('tags')
    .insert({ name, color, created_by: user.id })
    .select()
    .single()

  if (error) throw new Error(error.message)
  return data as Tag
}

export async function updateTag(id: string, { name, color }: { name: string; color: string }): Promise<Tag> {
  const { data, error } = await supabase
    .from('tags')
    .update({ name, color })
    .eq('id', id)
    .select()
    .single()

  if (error) throw new Error(error.message)
  return data as Tag
}

export async function deleteTag(id: string): Promise<void> {
  const { error } = await supabase
    .from('tags')
    .delete()
    .eq('id', id)

  if (error) throw new Error(error.message)
}

const LAST_USED_TAGS_MAX_AGE_MS = 48 * 60 * 60 * 1000

export async function fetchLastUsedTags(): Promise<Tag[]> {
  const { data: latest, error: latestErr } = await supabase
    .from('transaction_tags')
    .select('transaction_id, created_at')
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle()

  if (latestErr) throw new Error(latestErr.message)
  if (!latest || !latest.created_at) return []

  const lastUsedAt = new Date(latest.created_at).getTime()
  if (Number.isNaN(lastUsedAt) || Date.now() - lastUsedAt > LAST_USED_TAGS_MAX_AGE_MS) {
    return []
  }

  const { data, error } = await supabase
    .from('transaction_tags')
    .select('tag:tags(*)')
    .eq('transaction_id', latest.transaction_id)

  if (error) throw new Error(error.message)
  if (!data) return []

  return data
    .map((row) => {
      const tag = (row as { tag: Tag | Tag[] | null }).tag
      return Array.isArray(tag) ? tag[0] : tag
    })
    .filter((t): t is Tag => t != null)
}

export async function fetchTagUsage(id: string): Promise<{ count: number; lastUsedAt: string | null }> {
  const { data, error } = await supabase
    .from('transaction_tags')
    .select('created_at, transaction_id')
    .eq('tag_id', id)

  if (error) throw new Error(error.message)

  const rows = data ?? []
  const lastUsedAt = rows.length > 0
    ? rows.reduce((latest, r) => (r.created_at ?? '') > (latest ?? '') ? r.created_at : latest, rows[0].created_at)
    : null

  return { count: rows.length, lastUsedAt }
}
